//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//

import { useMemo } from 'react';

import { packageData } from 'utils/ErrorUtils';
import { makeUrl, replaceToken } from 'utils/UrlUtil';
import DataUrlConstants from 'utils/DataUrlConstants';

import { CardType } from 'pages/business-studio/business-dashboard/types';

import { injectEndpoints } from '../api';
import {
  BookmarkBusinessStudioFilterParams,
  BulkSubmitValidationReviewParams,
  BulkSubmitValidationReviewResponse,
  BusinessEntity,
  BusinessStudioEntityFilter,
  BusinessStudioFiltersResponse,
  BusinessStudioQueue,
  BusinessStudioQueueRecordsResponse,
  BusinessStudioRecordsResponse,
  CreateBusinessStudioFilterParams,
  CreateBusinessStudioRecordParams,
  CreateEntityLayoutParams,
  CurrencyOption,
  DeleteBusinessStudioFilterParams,
  DeleteEntityLayoutParams,
  DeleteRecordParams,
  EntityLayoutResponse,
  EntityReference,
  EntitySchemaResponse,
  GetBusinessStudioFiltersParams,
  GetBusinessStudioQueueRecordsParams,
  GetBusinessStudioRecordsParams,
  GetMergeEventParams,
  GetRecordAssociationsParams,
  GetQueueRecordParams,
  GetQueueRecordActionsParams,
  GetRecordParams,
  MergeEventResponse,
  QueueRecordAction,
  SubmitQueueRecordActionParams,
  SubmitQueueRecordResponse,
  RecordAssociationsResponse,
  RecordResponse,
  UpdateBusinessStudioFilterParams,
  UpdateBusinessStudioRecordParams,
  UpdateEntityLayoutParams,
  UpdateRecordAssociationsParams,
} from './types';
import { Entity } from 'store/entity/types';

const businessStudioApi = injectEndpoints({
  endpoints: (builder) => ({
    // 1. Get entities for dropdown
    // Using Business Studio API endpoint
    getBusinessStudioEntities: builder.query<BusinessEntity[], void>({
      query: () => ({
        url: 'arcade/api/v1/studio/business/entities',
      }),
      // Business Studio API returns entities array directly, not wrapped in an object
      providesTags: ['BusinessStudioEntity'],
    }),

    // 1b. Get queue tabs for an entity (dynamic tabs after "Golden Records")
    getBusinessStudioQueues: builder.query<BusinessStudioQueue[], string>({
      query: (entityId) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/queues', { entityId }),
      }),
      providesTags: (result, error, entityId) => [{ type: 'BusinessStudioEntity', id: `queues-${entityId}` }],
    }),

    // 2. Get entity layouts by entity ID
    getBusinessStudioEntityLayouts: builder.query<EntityLayoutResponse[], string>({
      query: (entityId) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/layouts', { entityId }),
      }),
      providesTags: (result, error, entityId) => [{ type: 'BusinessStudioLayout', id: entityId }],
    }),

    // 2b. Get entity schema with fields by entity ID
    getBusinessStudioEntitySchema: builder.query<EntitySchemaResponse, string>({
      query: (entityId) => ({
        url: makeUrl('arcade/api/v1/studio/schema/entity/{entityId}', { entityId }),
      }),
      providesTags: (result, error, entityId) => [{ type: 'BusinessStudioEntity', id: entityId }],
    }),

    // 2c. Get full entity definition (including picklist field values) for a single entity
    getBusinessStudioEntityDefinition: builder.query<Entity, string>({
      query: (entityId) => ({
        url: makeUrl('arcade/api/v1/schema/entity/{entityId}', { entityId }),
      }),
      // The /api/v1/schema/entity endpoint wraps the entity under data.entities[0]
      transformResponse: (response: { entities: Entity[] }) => response.entities[0],
      providesTags: (result, error, entityId) => [{ type: 'BusinessStudioEntity', id: `definition-${entityId}` }],
    }),

    // 3. Get records for selected entity (for grid)
    // Using Business Studio API endpoint
    getBusinessStudioEntityRecords: builder.query<BusinessStudioRecordsResponse, GetBusinessStudioRecordsParams>({
      query: ({ entityId, predicate, cursor, count = 25, direction = 'next', orderBy, sortDirection, page }) => {
        const preparedPredicate = predicate ? packageData(predicate) : undefined;

        return {
          url: makeUrl(
            `arcade/api/v1/studio/business/entity/{entityId}/records`,
            { entityId },
            {
              cursor,
              count,
              direction,
              orderBy,
              sortDirection,
              page,
            }
          ),
          method: 'POST',
          body: preparedPredicate,
          headers: {
            'Content-Type': 'text/plain',
          },
        };
      },
      providesTags: (result, error, arg) => [{ type: 'BusinessStudioRecord', id: arg.entityId }],
    }),

    // 3b. Get records for a specific queue tab
    getBusinessStudioQueueRecords: builder.query<
      BusinessStudioQueueRecordsResponse,
      GetBusinessStudioQueueRecordsParams
    >({
      query: (arg) => {
        const preparedPredicate = arg.predicate ? packageData(arg.predicate) : undefined;
        return {
          url: makeUrl(
            `arcade/api/v1/studio/business/entity/{entityId}/queue-records`,
            { entityId: arg.entityId },
            {
              queueId: arg.queueId,
              cursor: arg.cursor,
              count: arg.count,
              direction: arg.direction,
              ...(arg.orderBy && { orderBy: arg.orderBy }),
              ...(arg.sortDirection && { sortDirection: arg.sortDirection }),
            }
          ),
          method: 'POST',
          body: preparedPredicate,
          headers: {
            'Content-Type': 'text/plain',
          },
        };
      },
      providesTags: (result, error, arg) => [
        { type: 'BusinessStudioQueueRecord', id: `${arg.entityId}-${arg.queueId}` },
      ],
    }),

    // 4. Get single record by ID
    getBusinessStudioRecord: builder.query<RecordResponse, GetRecordParams>({
      query: ({ entityId, recordId }) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/records/{recordId}', {
          entityId,
          recordId,
        }),
      }),
      providesTags: (result, error, arg) => [{ type: 'BusinessStudioRecord', id: arg.recordId }],
    }),

    // 4b. Get single record with hidden fields included (for attribute pickers in card config forms).
    // Uses a separate cache key so it never overwrites the standard getBusinessStudioRecord cache entry.
    // keepUnusedDataFor: 0 ensures the response is discarded as soon as the subscriber unmounts.
    getBusinessStudioRecordWithHiddenFields: builder.query<RecordResponse, GetRecordParams>({
      query: ({ entityId, recordId }) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/records/{recordId}', {
          entityId,
          recordId,
        }),
        params: { includeHiddenFields: true },
      }),
      keepUnusedDataFor: 0,
    }),

    // 5. Get entity references for Table Card configuration
    // Used by the Add/Edit Table Card form to list available related entities
    getBusinessStudioEntityReferences: builder.query<EntityReference[], string>({
      query: (entityId) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/references', { entityId }),
      }),
    }),

    // 6a. Create a new record
    createBusinessStudioRecord: builder.mutation<RecordResponse, CreateBusinessStudioRecordParams>({
      query: ({ entityId, body, ignoreWarnings = false }) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/record', { entityId }),
        method: 'POST',
        body,
        params: { ignoreWarnings },
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'BusinessStudioRecord', id: arg.entityId }],
    }),

    // 6b. Update an existing record
    updateBusinessStudioRecord: builder.mutation<RecordResponse, UpdateBusinessStudioRecordParams>({
      query: ({ entityId, recordId, body, ignoreWarnings = false }) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/records/{recordId}', { entityId, recordId }),
        method: 'PUT',
        body,
        params: { ignoreWarnings },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'BusinessStudioRecord', id: arg.recordId },
        { type: 'BusinessStudioRecord', id: arg.entityId },
      ],
    }),

    // 6. Delete record by ID
    deleteBusinessStudioRecord: builder.mutation<void, DeleteRecordParams>({
      query: ({ entityId, recordId }) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/records/{recordId}', {
          entityId,
          recordId,
        }),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'BusinessStudioRecord', id: arg.recordId },
        { type: 'BusinessStudioRecord', id: arg.entityId },
      ],
    }),

    // 6c. Records the current record may be associated with, for the Manage Associations drawer
    getRecordAssociations: builder.query<RecordAssociationsResponse, GetRecordAssociationsParams>({
      query: ({ entityId, recordId, layoutId, cardId, count = 25, cursor, direction = 'next' }) => ({
        url: makeUrl(
          'arcade/api/v1/studio/business/entity/{entityId}/records/{recordId}/associations',
          { entityId, recordId },
          { layoutId, cardId, count, cursor, direction }
        ),
      }),
      providesTags: (result, error, arg) => [{ type: 'BusinessStudioRecord', id: `associations-${arg.recordId}` }],
    }),

    // 6d. Associate / disassociate related records from a table card
    updateRecordAssociations: builder.mutation<void, UpdateRecordAssociationsParams>({
      query: ({ entityId, recordId, layoutId, cardId, changes }) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/records/{recordId}/associations', {
          entityId,
          recordId,
        }),
        method: 'POST',
        body: { layoutId, cardId, changes },
      }),
      // The related entity's record list is what the card renders, so it has to be re-read;
      // the record itself is invalidated because its relationship fields move with the change
      invalidatesTags: (result, error, arg) => [
        { type: 'BusinessStudioRecord' as const, id: arg.recordId },
        { type: 'BusinessStudioRecord' as const, id: `associations-${arg.recordId}` },
        ...(arg.relatedEntity ? [{ type: 'BusinessStudioRecord' as const, id: arg.relatedEntity }] : []),
      ],
    }),

    // 7. Get single layout by layoutId
    getEntityLayoutById: builder.query<EntityLayoutResponse, { entityId: string; layoutId: string; recordId: string }>({
      query: ({ layoutId, recordId }) => ({
        url: makeUrl('arcade/api/v1/studio/business/layout/{layoutId}', { layoutId }, { recordId }),
      }),
      providesTags: (result, error, arg) => [
        { type: 'BusinessStudioLayout', id: arg.layoutId },
        { type: 'BusinessStudioLayout', id: arg.entityId },
      ],
    }),

    // 7. Create new dashboard layout for entity
    createEntityLayout: builder.mutation<EntityLayoutResponse, CreateEntityLayoutParams>({
      query: ({ entityId, entityName, name, displayName, description, isDefault, status, cards, cardPositions }) => {
        const payload = {
          entityId,
          entityName,
          name,
          displayName,
          description,
          isDefault,
          status,
          cards,
          cardPositions,
        };

        return {
          url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/layout', { entityId }),
          method: 'POST',
          body: payload,
        };
      },
      // Invalidate the entity-level layouts list so it includes the newly created layout
      invalidatesTags: (result, error, arg) => [{ type: 'BusinessStudioLayout', id: arg.entityId }],
    }),

    // 8. Update existing dashboard layout
    updateEntityLayout: builder.mutation<EntityLayoutResponse, UpdateEntityLayoutParams>({
      query: ({
        entityId,
        layoutId,
        entityName,
        name,
        displayName,
        description,
        isDefault,
        status,
        cards,
        cardPositions,
      }) => {
        const payload = {
          entityId,
          entityName,
          name,
          displayName,
          description,
          isDefault,
          status,
          cards,
          cardPositions,
        };

        return {
          url: makeUrl('arcade/api/v1/studio/business/layout/{layoutId}', {
            layoutId,
          }),
          method: 'PUT',
          body: payload,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'BusinessStudioLayout', id: arg.layoutId },
        { type: 'BusinessStudioLayout', id: arg.entityId },
      ],
    }),

    // 9. Delete dashboard layout
    deleteEntityLayout: builder.mutation<void, DeleteEntityLayoutParams>({
      query: ({ layoutId }) => ({
        url: makeUrl('arcade/api/v1/studio/business/layout/{layoutId}', {
          layoutId,
        }),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'BusinessStudioLayout', id: arg.layoutId },
        { type: 'BusinessStudioLayout', id: arg.entityId },
      ],
    }),

    // 10. Get filters for Business Studio
    getBusinessStudioFilters: builder.query<BusinessStudioFiltersResponse, GetBusinessStudioFiltersParams>({
      query: ({ entityId = null, bookmarked, cursor, count = 100, direction = 'next', orderBy, sortDirection }) => ({
        url: makeUrl(DataUrlConstants.GET_BUSINESS_STUDIO_FILTERS, null, {
          entityId,
          cursor,
          count,
          direction,
          bookmarked,
          ...(orderBy && { orderBy }),
          ...(sortDirection && { sortDirection }),
        }),
      }),
      providesTags: (result, error, arg) => [
        { type: 'BusinessStudioFilter', id: arg.entityId || 'LIST' },
        ...(result?.filters || []).map((filter) => ({ type: 'BusinessStudioFilter' as const, id: filter.id })),
      ],
    }),

    // 11. Create filter for Business Studio
    createBusinessStudioFilter: builder.mutation<BusinessStudioEntityFilter, CreateBusinessStudioFilterParams>({
      query: (params) => ({
        url: DataUrlConstants.CREATE_BUSINESS_STUDIO_FILTER,
        method: 'POST',
        body: params,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'BusinessStudioFilter', id: arg.syncariEntityId }],
    }),

    // 12. Update filter for Business Studio
    updateBusinessStudioFilter: builder.mutation<BusinessStudioEntityFilter, UpdateBusinessStudioFilterParams>({
      query: ({ filterId, filter }) => ({
        url: replaceToken(DataUrlConstants.UPDATE_BUSINESS_STUDIO_FILTER, { filterId }),
        method: 'PUT',
        body: filter,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'BusinessStudioFilter', id: arg.filterId },
        { type: 'BusinessStudioFilter', id: arg.filter.syncariEntityId },
      ],
    }),

    // 13. Delete filter for Business Studio
    deleteBusinessStudioFilter: builder.mutation<void, DeleteBusinessStudioFilterParams>({
      query: ({ filterId }) => ({
        url: replaceToken(DataUrlConstants.DELETE_BUSINESS_STUDIO_FILTER, { filterId }),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'BusinessStudioFilter', id: arg.filterId }],
    }),

    // 13a. Get a single queue record by queue item ID
    getQueueRecord: builder.query<RecordResponse, GetQueueRecordParams>({
      query: ({ entityId, queueItemId }) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/queue-records/{queueItemId}', {
          entityId,
          queueItemId,
        }),
      }),
      providesTags: (result, error, arg) => [
        { type: 'BusinessStudioQueueRecord', id: `${arg.entityId}-${arg.queueItemId}` },
      ],
    }),

    // 13b. Get actions available for a queue record item
    getQueueRecordActions: builder.query<QueueRecordAction[], GetQueueRecordActionsParams>({
      query: ({ entityId, queueItemId }) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/queue-records/{queueItemId}/actions', {
          entityId,
          queueItemId,
        }),
      }),
    }),

    submitQueueRecordAction: builder.mutation<SubmitQueueRecordResponse, SubmitQueueRecordActionParams>({
      query: ({ entityId, queueItemId, action, fields, ignoreWarnings }) => {
        const body: Record<string, any> = {};
        if (action) body.action = action;
        if (fields) body.fields = fields;
        return {
          url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/queue-records/{queueItemId}/submit', {
            entityId,
            queueItemId,
          }),
          method: 'POST',
          body,
          ...(ignoreWarnings && { params: { ignoreWarnings: true } }),
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'BusinessStudioQueueRecord', id: `${arg.entityId}-${arg.queueId}` },
        { type: 'BusinessStudioQueueRecord', id: `${arg.entityId}-${arg.queueItemId}` },
        { type: 'BusinessStudioEntity', id: `queues-${arg.entityId}` },
        'BusinessStudioEntity',
      ],
    }),

    bulkSubmitValidationReview: builder.mutation<BulkSubmitValidationReviewResponse, BulkSubmitValidationReviewParams>({
      query: ({ entityId, itemIds, action, fields }) => {
        const body: Record<string, any> = { itemIds };
        if (action) body.action = action;
        if (fields) body.fields = fields;
        return {
          url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/queue-records/bulk-submit', {
            entityId,
          }),
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'BusinessStudioQueueRecord', id: `${arg.entityId}-${arg.queueId}` },
        ...arg.itemIds.map((itemId) => ({
          type: 'BusinessStudioQueueRecord' as const,
          id: `${arg.entityId}-${itemId}`,
        })),
        { type: 'BusinessStudioEntity', id: `queues-${arg.entityId}` },
        'BusinessStudioEntity',
      ],
    }),

    // 14. Get supported currencies for the currency formatter
    // commonFirst=true puts the most commonly used currencies at the top of the list
    getCurrencies: builder.query<CurrencyOption[], void>({
      query: () => ({
        url: 'arcade/api/v1/currencies?commonFirst=true',
      }),
    }),

    // 15. Bookmark/unbookmark filter for Business Studio
    bookmarkBusinessStudioFilter: builder.mutation<void, BookmarkBusinessStudioFilterParams>({
      query: ({ filterId, bookmarked }) => ({
        url: replaceToken(DataUrlConstants.BOOKMARK_BUSINESS_STUDIO_FILTER, { filterId }),
        method: 'PATCH',
        body: bookmarked,
        headers: { 'content-type': 'application/json' },
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'BusinessStudioFilter', id: arg.filterId }],
    }),

    // 16. Get merge event detail for a merge review record
    // Returns all conflicting records that are part of this merge event,
    // along with metadata (field labels/types) and per-record recommended fields.
    getMergeEvent: builder.query<MergeEventResponse, GetMergeEventParams>({
      query: ({ entityId, mergeRecordId }) => ({
        url: makeUrl('arcade/api/v1/studio/business/entity/{entityId}/merge-records/{mergeRecordId}', {
          entityId,
          mergeRecordId,
        }),
      }),
      providesTags: (result, error, arg) => [
        { type: 'BusinessStudioQueueRecord', id: `merge-event-${arg.entityId}-${arg.mergeRecordId}` },
      ],
    }),
  }),
});

export const {
  useGetBusinessStudioQueuesQuery,
  useLazyGetBusinessStudioQueuesQuery,
  useGetBusinessStudioQueueRecordsQuery,
  useLazyGetBusinessStudioQueueRecordsQuery,
  useCreateBusinessStudioRecordMutation,
  useUpdateBusinessStudioRecordMutation,
  useGetCurrenciesQuery,
  useGetBusinessStudioEntitiesQuery,
  useLazyGetBusinessStudioEntitiesQuery,
  useGetBusinessStudioEntityLayoutsQuery,
  useLazyGetBusinessStudioEntityLayoutsQuery,
  useGetBusinessStudioEntitySchemaQuery,
  useLazyGetBusinessStudioEntitySchemaQuery,
  useGetBusinessStudioEntityDefinitionQuery,
  useGetBusinessStudioEntityRecordsQuery,
  useLazyGetBusinessStudioEntityRecordsQuery,
  useGetBusinessStudioRecordQuery: useGetBusinessStudioRecordBaseQuery,
  useLazyGetBusinessStudioRecordQuery,
  useGetBusinessStudioRecordWithHiddenFieldsQuery,
  useLazyGetBusinessStudioRecordWithHiddenFieldsQuery,
  useGetBusinessStudioEntityReferencesQuery,
  useLazyGetBusinessStudioEntityReferencesQuery,
  useDeleteBusinessStudioRecordMutation,
  useGetRecordAssociationsQuery,
  useUpdateRecordAssociationsMutation,
  useGetEntityLayoutByIdQuery,
  useLazyGetEntityLayoutByIdQuery,
  useCreateEntityLayoutMutation,
  useUpdateEntityLayoutMutation,
  useDeleteEntityLayoutMutation,
  useGetBusinessStudioFiltersQuery,
  useLazyGetBusinessStudioFiltersQuery,
  useCreateBusinessStudioFilterMutation,
  useUpdateBusinessStudioFilterMutation,
  useDeleteBusinessStudioFilterMutation,
  useBookmarkBusinessStudioFilterMutation,
  useGetQueueRecordQuery,
  useLazyGetQueueRecordQuery,
  useGetQueueRecordActionsQuery,
  useLazyGetQueueRecordActionsQuery,
  useSubmitQueueRecordActionMutation,
  useBulkSubmitValidationReviewMutation,
  useGetMergeEventQuery,
  useLazyGetMergeEventQuery,
  util: businessStudioApiUtil,
} = businessStudioApi;

/**
 * Wraps getBusinessStudioRecord + getBusinessStudioRecordWithHiddenFields, calling both in
 * parallel and merging the results: metadata (visible fields only) from the base query and
 * record values (all fields including hidden) from the hidden-fields query.
 *
 * Drop-in replacement for the generated useGetBusinessStudioRecordBaseQuery hook.
 */
export const useGetBusinessStudioRecordQuery = (args: GetRecordParams, options?: { skip?: boolean }) => {
  const skip = options?.skip ?? false;

  const base = useGetBusinessStudioRecordBaseQuery(args, { skip });
  const withHidden = useGetBusinessStudioRecordWithHiddenFieldsQuery(args, { skip });

  const mergedData = useMemo(() => {
    if (!base.data && !withHidden.data) {
      return undefined;
    }
    return {
      ...base.data,
      record: withHidden.data?.record ?? base.data?.record,
    } as typeof base.data;
  }, [base.data, withHidden.data]);

  // `data.metadata` stays visible-only, because callers that render a row per entry (the record
  // drawer) must not grow rows for hidden fields. Callers that were handed a specific field to
  // display need the hidden ones too: without their metadata there is no way to tell whether a
  // configured name is a field's api name or its id, and so no way to find its value.
  const metadataIncludingHidden = useMemo(() => withHidden.data?.metadata ?? base.data?.metadata, [
    withHidden.data,
    base.data,
  ]);

  const refetch = useMemo(
    () => () => {
      base.refetch();
      withHidden.refetch();
    },
    // refetch functions from RTK Query are stable references — safe to omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return useMemo(
    () => ({
      ...base,
      data: mergedData,
      metadataIncludingHidden,
      isLoading: base.isLoading || withHidden.isLoading,
      isFetching: base.isFetching || withHidden.isFetching,
      isSuccess: base.isSuccess && withHidden.isSuccess,
      isError: base.isError || withHidden.isError,
      error: base.error ?? withHidden.error,
      refetch,
    }),
    [
      mergedData,
      metadataIncludingHidden,
      base.isLoading,
      base.isFetching,
      base.isSuccess,
      base.isError,
      base.error,
      withHidden.isLoading,
      withHidden.isFetching,
      withHidden.isSuccess,
      withHidden.isError,
      withHidden.error,
      refetch,
    ]
  );
};
